import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FeedbackWidget, AutoFeedbackWidget } from './feedback-widget';
import { FeedbackButton } from './feedback-button';

/**
 * Demo component to showcase feedback functionality
 * This is for testing purposes and can be removed in production
 */
export function FeedbackDemo() {
  const handleFeedbackSubmit = (feedback: string) => {
    console.log('Demo feedback received:', feedback);
  };

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader>
          <CardTitle>Feedback Components Demo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Combined Widget Demo */}
          <div className="space-y-2">
            <h3 className="font-semibold">Combined Feedback Widget</h3>
            <p className="text-sm text-muted-foreground">
              Complete feedback solution with button and popover
            </p>
            <div className="relative h-32 bg-muted/30 rounded-lg border-2 border-dashed border-muted-foreground/20 flex items-center justify-center">
              <span className="text-muted-foreground">Annotation Interface Area</span>
              <FeedbackWidget
                context="annotation"
                sessionId="demo-session-123"
                onFeedbackSubmit={handleFeedbackSubmit}
                position="relative"
                className="absolute bottom-2 right-2"
              />
            </div>
          </div>

          {/* Auto-detecting Widget */}
          <div className="space-y-2">
            <h3 className="font-semibold">Auto-detecting Widget</h3>
            <p className="text-sm text-muted-foreground">
              Automatically detects context from current URL
            </p>
            <div className="relative h-32 bg-muted/30 rounded-lg border-2 border-dashed border-muted-foreground/20 flex items-center justify-center">
              <span className="text-muted-foreground">Auto-context Detection</span>
              <AutoFeedbackWidget
                sessionId="auto-demo-456"
                onFeedbackSubmit={handleFeedbackSubmit}
                position="relative"
                className="absolute bottom-2 right-2"
              />
            </div>
          </div>

          {/* Different Contexts */}
          <div className="space-y-4">
            <h3 className="font-semibold">Context Variations</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Demographics Context */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    Demographics
                    <Badge variant="outline">Context</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative h-24 bg-blue-50 dark:bg-blue-950/20 rounded border flex items-center justify-center">
                    <span className="text-xs text-muted-foreground">Demographics Form</span>
                    <FeedbackWidget
                      context="demographics"
                      sessionId="demo-demographics"
                      onFeedbackSubmit={handleFeedbackSubmit}
                      position="relative"
                      className="absolute bottom-1 right-1 scale-75"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Annotation Context */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    Annotation
                    <Badge variant="outline">Context</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative h-24 bg-green-50 dark:bg-green-950/20 rounded border flex items-center justify-center">
                    <span className="text-xs text-muted-foreground">Music Annotation</span>
                    <FeedbackWidget
                      context="annotation"
                      sessionId="demo-annotation"
                      onFeedbackSubmit={handleFeedbackSubmit}
                      position="relative"
                      className="absolute bottom-1 right-1 scale-75"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Completion Context */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    Completion
                    <Badge variant="outline">Context</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative h-24 bg-purple-50 dark:bg-purple-950/20 rounded border flex items-center justify-center">
                    <span className="text-xs text-muted-foreground">Session Complete</span>
                    <FeedbackWidget
                      context="completion"
                      sessionId="demo-completion"
                      onFeedbackSubmit={handleFeedbackSubmit}
                      position="relative"
                      className="absolute bottom-1 right-1 scale-75"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Standalone Button */}
          <div className="space-y-2">
            <h3 className="font-semibold">Standalone Button</h3>
            <p className="text-sm text-muted-foreground">
              Button component without integrated popover
            </p>
            <div className="flex gap-2">
              <FeedbackButton
                context="annotation"
                sessionId="standalone-demo"
                onClick={() => alert('Standalone button clicked!')}
                position="relative"
              />
              <FeedbackButton
                context="demographics"
                sessionId="standalone-demo-2"
                onClick={() => alert('Demographics button clicked!')}
                position="relative"
                disabled
              />
            </div>
          </div>

          {/* Usage Instructions */}
          <div className="space-y-2 pt-4 border-t">
            <h3 className="font-semibold">Usage Instructions</h3>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>• Click any feedback button to open the feedback popover</p>
              <p>• Type your feedback (max 500 characters)</p>
              <p>• Use Ctrl+Enter to submit quickly</p>
              <p>• Press Escape to close the popover</p>
              <p>• Context determines the placeholder text and title</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}